import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { personsApi } from '../../api/persons';
import type { PersonDto, CreatePersonRequest } from '../../types/person';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { useReportScreen } from '../../components/ScreenContext';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useIsMirror } from '../../hooks/useIsMirror';

type RoleFilter = '' | 'client' | 'supplier' | 'worker' | 'both' | 'any';

const ROLE_FILTER_OPTIONS: { value: RoleFilter; labelKey: string }[] = [
  { value: '', labelKey: 'persons.allContacts' },
  { value: 'client', labelKey: 'persons.customerOnly' },
  { value: 'supplier', labelKey: 'persons.vendorOnly' },
  { value: 'worker', labelKey: 'persons.employeeOnly' },
  { value: 'both', labelKey: 'persons.custPlusVend' },
  { value: 'any', labelKey: 'persons.hasAnyRole' },
];

export default function PersonList() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [filter, setFilter] = useState<RoleFilter>('');
  const [showForm, setShowForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState<PersonDto | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // ADR-004: mirror workspaces (mysql, asmx-owned) are READ-ONLY — hide the
  // write affordances; the API 403s them anyway (defense in depth).
  const isMirror = useIsMirror();

  const { data: persons, isLoading } = useQuery({
    queryKey: ['persons', debouncedSearch, filter],
    queryFn: () => personsApi.list({ search: debouncedSearch || undefined, type: filter || undefined }),
  });

  // Deep link from findings (e.g. the customer drill's "Redaguoti kontaktą"):
  // ?edit={id} opens the existing edit form directly.
  useEffect(() => {
    const editId = Number(searchParams.get('edit'));
    if (!editId || persons === undefined) return;
    const target = persons.find(p => p.id === editId);
    if (target) {
      setEditingPerson(target);
      setShowForm(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, persons, setSearchParams]);

  const createMutation = useMutation({
    mutationFn: (data: CreatePersonRequest) => personsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['persons'] });
      setShowForm(false);
    },
    onError: (err: Error) => alert(`Error: ${err.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => personsApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['persons'] });
      addToast(t('persons.personDeleted', { id }), 'success');
    },
    onError: (err: Error) => alert(`Error: ${err.message}`),
  });

  // All hooks must be above any early return (Rules of Hooks)
  const filteredPersons = useMemo(() => {
    if (!persons) return [];
    if (filter === 'both') {
      return persons.filter(p => p.isClient && p.isSupplier);
    }
    if (filter === 'any') {
      return persons.filter(p => p.isClient || p.isSupplier || p.isWorker);
    }
    return persons;
  }, [persons, filter]);

  const roleStats = useMemo(() => {
    const all = persons ?? [];
    return {
      total: all.length,
      customers: all.filter(p => p.isClient).length,
      vendors: all.filter(p => p.isSupplier).length,
      employees: all.filter(p => p.isWorker).length,
      customerVendors: all.filter(p => p.isClient && p.isSupplier).length,
    };
  }, [persons]);

  const personCount = filteredPersons.length;
  const personPreview = useMemo(() =>
    (filteredPersons).slice(0, 5).map(p => {
      const roles: string[] = [];
      if (p.isClient) roles.push('Customer');
      if (p.isSupplier) roles.push('Vendor');
      if (p.isWorker) roles.push('Employee');
      return `${p.name} (${roles.join('+') || 'No role'})`;
    }), [filteredPersons]);

  useReportScreen(`Kontaktai (${personCount} irasu)`, {
    recordCount: personCount,
    filters: filter ? { role: filter } : undefined,
    preview: personPreview,
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('persons.title')}</h1>
          <p className="text-xs text-gray-500 mt-0.5">{t('persons.subtitle')}</p>
        </div>
        {!isMirror && (
          <button
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
            onClick={() => { setShowForm(true); setEditingPerson(null); }}
          >
            {t('persons.addContact')}
          </button>
        )}
      </div>

      {/* Role summary strip */}
      <div className="grid grid-cols-5 gap-2">
        <RoleStatCard label={t('persons.total')} count={roleStats.total} color="gray" />
        <RoleStatCard label={t('persons.customers')} count={roleStats.customers} color="blue" />
        <RoleStatCard label={t('persons.vendors')} count={roleStats.vendors} color="amber" />
        <RoleStatCard label={t('persons.employees')} count={roleStats.employees} color="green" />
        <RoleStatCard label={t('persons.custVend')} count={roleStats.customerVendors} color="purple" />
      </div>

      {/* Search and filter */}
      <div className="flex gap-3">
        <input
          type="text"
          placeholder={t('persons.searchPlaceholder')}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="px-3 py-2 text-sm border border-gray-300 rounded-md"
          value={filter}
          onChange={(e) => setFilter(e.target.value as RoleFilter)}
        >
          {ROLE_FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
          ))}
        </select>
      </div>

      {/* Create/Edit form */}
      {(showForm || editingPerson) && !isMirror && (
        <PersonForm
          person={editingPerson}
          onSubmit={(data) => {
            if (editingPerson) {
              personsApi.update(editingPerson.id, { ...data, currencyCode: data.currencyCode ?? 'EUR' }).then(() => {
                queryClient.invalidateQueries({ queryKey: ['persons'] });
                setEditingPerson(null);
              }).catch((err: Error) => alert(`Error: ${err.message}`));
            } else {
              createMutation.mutate(data);
            }
          }}
          onCancel={() => { setShowForm(false); setEditingPerson(null); }}
          saving={createMutation.isPending}
        />
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="dense w-full">
          <thead>
            <tr>
              <th className="text-left">{t('common.name')}</th>
              <th className="text-left">{t('common.code')}</th>
              <th className="text-left">{t('settings.vatCode')}</th>
              <th className="text-right">{t('persons.receivables')}</th>
              <th className="text-right">{t('persons.payables')}</th>
              <th className="text-left">{t('persons.roles')}</th>
              <th className="text-left">{t('common.status')}</th>
              <th className="text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredPersons.map((p) => (
              <tr key={p.id} className="border-b border-gray-50">
                <td className="font-medium">{p.name}</td>
                <td>{p.code || '-'}</td>
                <td>{p.vatCode || '-'}</td>
                <td className="num">{p.receivablesAccount || '-'}</td>
                <td className="num">{p.payablesAccount || '-'}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {p.isClient && <RoleBadge label={t('persons.customer')} color="blue" />}
                    {p.isSupplier && <RoleBadge label={t('persons.vendor')} color="amber" />}
                    {p.isWorker && <RoleBadge label={t('persons.employee')} color="green" />}
                    {!p.isClient && !p.isSupplier && !p.isWorker && (
                      <span className="text-xs text-gray-400 italic">{t('persons.noRole')}</span>
                    )}
                    {p.isClient && p.isSupplier && (
                      <RoleBadge label={t('persons.both')} color="purple" />
                    )}
                  </div>
                </td>
                <td>
                  {p.isObsolete ? (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                      {t('persons.inactive')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      {t('common.active')}
                    </span>
                  )}
                </td>
                <td className="text-right space-x-2">
                  {!isMirror && (
                    <>
                      <button
                        className="text-xs text-primary-600 hover:underline"
                        onClick={() => { setEditingPerson(p); setShowForm(false); }}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        className="text-xs text-red-500 hover:underline"
                        onClick={() => { if (confirm(t('persons.deleteConfirm', { name: p.name }))) deleteMutation.mutate(p.id); }}
                      >
                        {t('common.delete')}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {filteredPersons.length === 0 && (
              <tr><td colSpan={8} className="text-center text-gray-400 py-8">{t('persons.noContacts')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'bg-primary-50', text: 'text-primary-700' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-700' },
  green: { bg: 'bg-green-50', text: 'text-green-700' },
  purple: { bg: 'bg-slate-100', text: 'text-slate-600' },
};

function RoleBadge({ label, color }: { label: string; color: string }) {
  const fallback = { bg: 'bg-primary-50', text: 'text-primary-700' };
  const c = ROLE_COLORS[color] ?? fallback;
  return (
    <span className={`inline-block px-1.5 py-0.5 text-xs ${c.bg} ${c.text} rounded font-medium`}>
      {label}
    </span>
  );
}

function RoleStatCard({ label, count, color }: { label: string; count: number; color: string }) {
  const colorClasses: Record<string, string> = {
    gray: 'bg-gray-50 border-gray-200',
    blue: 'bg-primary-50 border-primary-200',
    amber: 'bg-amber-50 border-amber-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-slate-50 border-slate-200',
  };
  return (
    <div className={`rounded-lg border p-2 text-center ${colorClasses[color] ?? colorClasses.gray}`}>
      <div className="text-lg font-bold text-gray-900">{count}</div>
      <div className="text-xs text-gray-600">{label}</div>
    </div>
  );
}

function PersonForm({ person, onSubmit, onCancel, saving }: {
  person: PersonDto | null;
  onSubmit: (data: CreatePersonRequest) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreatePersonRequest>({
    name: person?.name ?? '',
    code: person?.code ?? '',
    vatCode: person?.vatCode ?? '',
    address: person?.address ?? '',
    receivablesAccount: person?.receivablesAccount ?? 0,
    payablesAccount: person?.payablesAccount ?? 0,
    currencyCode: person?.currencyCode ?? 'EUR',
    isClient: person?.isClient ?? true,
    isSupplier: person?.isSupplier ?? false,
    isWorker: person?.isWorker ?? false,
    isNaturalPerson: person?.isNaturalPerson ?? false,
    street: person?.street ?? '',
    city: person?.city ?? '',
    postalCode: person?.postalCode ?? '',
    country: person?.country ?? 'LT',
    contactName: person?.contactName ?? '',
    contactPhone: person?.contactPhone ?? '',
    email: person?.email ?? '',
    paymentTermsDays: person?.paymentTermsDays ?? 30,
    discountPercent: person?.discountPercent ?? 0,
    creditLimit: person?.creditLimit ?? 0,
    sector: person?.sector ?? '',
    notes: person?.notes ?? '',
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900 mb-3">{person ? t('persons.editContact') : t('persons.newContact')}</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {/* Basic info */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('common.name')} *</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('common.code')}</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('settings.vatCode')}</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.vatCode} onChange={(e) => setForm({ ...form, vatCode: e.target.value })} />
        </div>

        {/* Structured address */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.street')}</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.city')}</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('persons.postalCode')}</label>
            <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('persons.country')}</label>
            <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
          </div>
        </div>

        {/* Contact person */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.contactName')}</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.contactPhone')}</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.contactEmail')}</label>
          <input type="email" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>

        {/* Accounts */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.receivablesAccount')}</label>
          <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.receivablesAccount} onChange={(e) => setForm({ ...form, receivablesAccount: parseInt(e.target.value) || 0 })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.payablesAccount')}</label>
          <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.payablesAccount} onChange={(e) => setForm({ ...form, payablesAccount: parseInt(e.target.value) || 0 })} />
        </div>

        {/* Business terms */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.paymentTermsDays')}</label>
          <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.paymentTermsDays} onChange={(e) => setForm({ ...form, paymentTermsDays: parseInt(e.target.value) || 30 })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.discountPercent')}</label>
          <input type="number" step="0.01" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.discountPercent} onChange={(e) => setForm({ ...form, discountPercent: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.creditLimit')}</label>
          <input type="number" step="0.01" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.creditLimit} onChange={(e) => setForm({ ...form, creditLimit: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('persons.sector')}</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} />
        </div>

        {/* Notes */}
        <div className="col-span-2 md:col-span-3">
          <label className="block text-xs text-gray-500 mb-1">{t('persons.notes')}</label>
          <textarea
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            rows={2}
            maxLength={form.isNaturalPerson ? 250 : 1000}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          <div className="flex justify-between text-xs text-gray-400 mt-0.5">
            <span>
              {form.isNaturalPerson
                ? 'For natural persons, notes are limited to 250 characters. Avoid entering personal data.'
                : ''}
            </span>
            <span>{(form.notes ?? '').length}/{form.isNaturalPerson ? 250 : 1000}</span>
          </div>
        </div>

        {/* Roles and actions */}
        <div className="flex items-end gap-3">
          <label className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={form.isClient} onChange={(e) => setForm({ ...form, isClient: e.target.checked })} /> {t('persons.customer')}
          </label>
          <label className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={form.isSupplier} onChange={(e) => setForm({ ...form, isSupplier: e.target.checked })} /> {t('persons.vendor')}
          </label>
          <label className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={form.isWorker} onChange={(e) => setForm({ ...form, isWorker: e.target.checked })} /> {t('persons.employee')}
          </label>
          <label className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={form.isNaturalPerson} onChange={(e) => setForm({ ...form, isNaturalPerson: e.target.checked })} /> {t('persons.naturalPerson')}
          </label>
        </div>
        <div className="flex items-end gap-2">
          <button
            className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            onClick={() => onSubmit(form)} disabled={saving || !form.name}
          >
            {saving ? t('common.saving') : (person ? t('persons.update') : t('common.create'))}
          </button>
          <button
            className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
